import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Drawer, Button, FormField, Input, Select, Switch, Textarea } from "@/components/ui";
import { BlockMetadataFields } from "./BlockMetadataFields";
import {
  HOME_BLOCK_TYPES,
  HOME_BLOCK_TYPE_LABELS,
  homeBlocksApi,
  type HomeBlock,
  type HomeBlockInput,
  type HomeBlockType,
} from "@/lib/api";
import {
  fromDatetimeLocalValue,
  getNowDatetimeLocalValue,
  isPastDatetimeLocalValue,
  toDatetimeLocalValue,
} from "@/lib/utils/datetimeLocal";

type Preset = "image" | "text" | "image_text" | "advanced";
type ActionMode = "none" | "route" | "link";
type PublicationMode = "always" | "until" | "period";

const INTERNAL_ROUTES = [
  "/",
  "/eventos",
  "/biblia",
  "/contenido",
  "/ministerios",
  "/primeros-pasos",
  "/preparar-visita",
  "/sedes",
  "/contacto",
] as const;

export interface HomeBlockFormDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: HomeBlockInput) => Promise<void>;
  editingBlock: HomeBlock | null;
  nextOrder: number;
}

function blankForm(order: number, preset: Preset = "image"): HomeBlockInput {
  return {
    type: preset === "image" ? "image_banner" : preset === "text" ? "announcement" : "custom_banner",
    enabled: true,
    order,
    title: "",
    subtitle: null,
    body: "",
    imageUrl: null,
    actionLabel: null,
    actionTarget: null,
    startsAt: null,
    endsAt: null,
    status: "draft",
    metadata: {},
  };
}

function fromBlock(block: HomeBlock): HomeBlockInput {
  return {
    type: block.type,
    enabled: block.enabled,
    order: block.order,
    title: block.title,
    subtitle: block.subtitle,
    body: block.body,
    imageUrl: block.imageUrl,
    actionLabel: block.actionLabel,
    actionTarget: block.actionTarget,
    startsAt: block.startsAt,
    endsAt: block.endsAt,
    status: block.status,
    metadata: block.metadata,
  };
}

function presetFromBlock(block: HomeBlock | null): Preset {
  if (!block) return "image";
  if (block.type === "image_banner") return "image";
  if (block.type === "announcement" && !block.imageUrl) return "text";
  if (block.type === "custom_banner") return "image_text";
  return "advanced";
}

function actionModeFromTarget(target: string | null | undefined): ActionMode {
  if (!target) return "none";
  return target.startsWith("https://") ? "link" : "route";
}

function formatBytes(size: number | null) {
  if (!size) return null;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function isWideImage(width: number | null, height: number | null) {
  if (!width || !height) return true;
  const ratio = width / height;
  return ratio >= 1.55 && ratio <= 2.0;
}

export function HomeBlockFormDrawer({
  open,
  onClose,
  onSubmit,
  editingBlock,
  nextOrder,
}: HomeBlockFormDrawerProps) {
  const [preset, setPreset] = useState<Preset>(() => presetFromBlock(editingBlock));
  const [form, setForm] = useState<HomeBlockInput>(() =>
    editingBlock ? fromBlock(editingBlock) : blankForm(nextOrder),
  );
  const [publicationMode, setPublicationMode] = useState<PublicationMode>("always");
  const [actionMode, setActionMode] = useState<ActionMode>("none");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageInfo, setImageInfo] = useState<{ size: number | null; width: number | null; height: number | null }>({
    size: null,
    width: null,
    height: null,
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const contentLocked = editingBlock?.source === "system";
  const isSimpleImage = preset === "image" && !contentLocked;

  useEffect(() => {
    if (open) {
      const nextPreset = presetFromBlock(editingBlock);
      const next = editingBlock ? fromBlock(editingBlock) : blankForm(nextOrder, nextPreset);
      setPreset(nextPreset);
      setForm(next);
      setPublicationMode(next.startsAt ? "period" : next.endsAt ? "until" : "always");
      setActionMode(actionModeFromTarget(next.actionTarget));
      setAdvancedOpen(false);
      setImageInfo({ size: null, width: null, height: null });
      setError(null);
    }
  }, [open, editingBlock, nextOrder]);

  const imagePreview = useMemo(() => form.imageUrl ?? null, [form.imageUrl]);

  function updatePreset(nextPreset: Preset) {
    setPreset(nextPreset);
    setAdvancedOpen(nextPreset === "advanced");
    if (!editingBlock) {
      setForm(blankForm(nextOrder, nextPreset));
      setActionMode("none");
      setPublicationMode("always");
      setImageInfo({ size: null, width: null, height: null });
      return;
    }
    setForm((current) => ({
      ...current,
      type: nextPreset === "image" ? "image_banner" : nextPreset === "text" ? "announcement" : current.type,
    }));
  }

  async function uploadFile(file: File) {
    setError(null);
    setUploading(true);
    try {
      const result = await homeBlocksApi.uploadHomeBlockMedia(file);
      setForm((current) => ({ ...current, imageUrl: result.url }));
      setImageInfo({ size: result.size, width: result.width, height: result.height });
    } catch {
      setError("No se pudo subir la imagen. Usa JPG, PNG o WebP de maximo 5 MB.");
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const payload = { ...form };
    if (publicationMode === "always") {
      payload.startsAt = null;
      payload.endsAt = null;
    } else if (publicationMode === "until") {
      payload.startsAt = new Date().toISOString();
      if (!payload.endsAt) {
        setError("Indica hasta cuando sera visible el bloque.");
        return;
      }
      if (new Date(payload.endsAt).getTime() <= Date.now() - 60_000) {
        setError("La hora final no puede estar ya pasada.");
        return;
      }
    } else {
      if (!payload.startsAt || !payload.endsAt) {
        setError("Indica inicio y final del periodo.");
        return;
      }
      if (new Date(payload.endsAt).getTime() <= new Date(payload.startsAt).getTime()) {
        setError("La hora final debe ser posterior al inicio.");
        return;
      }
      const unchangedWindow =
        editingBlock?.startsAt === payload.startsAt &&
        editingBlock?.endsAt === payload.endsAt;
      if (!unchangedWindow && new Date(payload.endsAt).getTime() <= Date.now() - 60_000) {
        setError("No se puede guardar una ventana ya finalizada.");
        return;
      }
    }
    if (preset === "image") {
      payload.type = "image_banner";
      payload.subtitle = null;
      payload.body = null;
      payload.actionLabel = null;
      if (!payload.imageUrl) {
        setError("Sube una imagen antes de guardar el anuncio.");
        return;
      }
    }
    if (actionMode === "none") {
      payload.actionTarget = null;
      payload.actionLabel = null;
    } else if (actionMode === "route") {
      payload.actionTarget = payload.actionTarget || "/";
    } else if (payload.actionTarget && !payload.actionTarget.startsWith("https://")) {
      setError("Los enlaces externos deben empezar por https://");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(payload);
      onClose();
    } catch {
      setError("No se pudo guardar el bloque. Revisa los datos e intentalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title={editingBlock ? "Editar bloque" : "Nuevo bloque"}>
      <form onSubmit={handleSubmit} className="space-y-4 pb-4">
        {!editingBlock && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-ink">Que quieres crear?</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ["image", "Anuncio con imagen"],
                ["text", "Anuncio con texto"],
                ["image_text", "Imagen + texto + boton"],
                ["advanced", "Avanzado"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`rounded-lg border px-3 py-2 text-left text-sm ${
                    preset === value ? "border-brand-500 bg-brand-50 text-brand-800" : "border-ink/10 bg-white text-ink"
                  }`}
                  onClick={() => updatePreset(value as Preset)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        <FormField label="Nombre interno" required>
          {(field) => (
            <Input
              {...field}
              value={form.title ?? ""}
              disabled={contentLocked}
              onChange={(e) => setForm({ ...form, title: e.target.value || null })}
              placeholder="Campamento jovenes"
              required
            />
          )}
        </FormField>

        {(preset === "image" || preset === "image_text" || preset === "advanced") && (
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium text-ink">Imagen</p>
              <p className="text-xs text-ink-faint">16:9 recomendado, 1600 x 900 px, JPG / PNG / WebP, maximo 5 MB.</p>
            </div>
            {imagePreview && (
              <div className="overflow-hidden rounded-lg border border-ink/10 bg-surface-subtle">
                <img src={imagePreview} alt="" className="aspect-video w-full object-contain" />
              </div>
            )}
            {imageInfo.width && imageInfo.height && (
              <p className="text-xs text-ink-faint">
                {formatBytes(imageInfo.size)} · {imageInfo.width} x {imageInfo.height} px
                {!isWideImage(imageInfo.width, imageInfo.height) ? " · La proporcion se aleja de 16:9; se mostrara completa sin recorte." : ""}
              </p>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void uploadFile(file);
                event.currentTarget.value = "";
              }}
            />
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} loading={uploading} disabled={contentLocked}>
                {form.imageUrl ? "Cambiar imagen" : "Subir imagen"}
              </Button>
              {form.imageUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setForm({ ...form, imageUrl: null });
                    setImageInfo({ size: null, width: null, height: null });
                  }}
                  disabled={contentLocked}
                >
                  Eliminar
                </Button>
              )}
            </div>
          </div>
        )}

        {(preset === "text" || preset === "image_text" || preset === "advanced") && (
          <>
            <FormField label="Subtitulo">
              {(field) => <Input {...field} value={form.subtitle ?? ""} disabled={contentLocked} onChange={(e) => setForm({ ...form, subtitle: e.target.value || null })} />}
            </FormField>
            <FormField label="Contenido">
              {(field) => <Textarea {...field} value={form.body ?? ""} disabled={contentLocked} onChange={(e) => setForm({ ...form, body: e.target.value || null })} />}
            </FormField>
          </>
        )}

        <FormField label="Visibilidad">
          {(field) => (
            <Select
              {...field}
              value={publicationMode}
              disabled={contentLocked}
              onChange={(e) => {
                const nextMode = e.target.value as PublicationMode;
                if (nextMode === "always") setForm({ ...form, startsAt: null, endsAt: null });
                if (nextMode === "until") setForm({ ...form, startsAt: null });
                setPublicationMode(nextMode);
              }}
            >
              <option value="always">Siempre</option>
              <option value="until">Desde ahora hasta...</option>
              <option value="period">Programar periodo</option>
            </Select>
          )}
        </FormField>

        {publicationMode === "until" && (
          <FormField label="Visible hasta" hint="Hora de Madrid">
            {(field) => (
              <Input
                {...field}
                type="datetime-local"
                disabled={contentLocked}
                min={getNowDatetimeLocalValue()}
                value={toDatetimeLocalValue(form.endsAt)}
                onChange={(e) => setForm({ ...form, endsAt: fromDatetimeLocalValue(e.target.value) })}
                aria-invalid={isPastDatetimeLocalValue(toDatetimeLocalValue(form.endsAt))}
              />
            )}
          </FormField>
        )}

        {publicationMode === "period" && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="Visible desde" hint="Hora de Madrid">
              {(field) => <Input {...field} type="datetime-local" disabled={contentLocked} min={getNowDatetimeLocalValue()} value={toDatetimeLocalValue(form.startsAt)} onChange={(e) => setForm({ ...form, startsAt: fromDatetimeLocalValue(e.target.value) })} />}
            </FormField>
            <FormField label="Visible hasta" hint="Hora de Madrid">
              {(field) => <Input {...field} type="datetime-local" disabled={contentLocked} min={toDatetimeLocalValue(form.startsAt) || getNowDatetimeLocalValue()} value={toDatetimeLocalValue(form.endsAt)} onChange={(e) => setForm({ ...form, endsAt: fromDatetimeLocalValue(e.target.value) })} />}
            </FormField>
          </div>
        )}

        {isSimpleImage && (
          <FormField label="Al tocar el anuncio">
            {(field) => (
              <Select {...field} value={actionMode} onChange={(e) => setActionMode(e.target.value as ActionMode)}>
                <option value="none">No hacer nada</option>
                <option value="route">Abrir una pagina de IESAPP</option>
                <option value="link">Abrir enlace</option>
              </Select>
            )}
          </FormField>
        )}

        {(actionMode === "route" || preset === "image_text") && (
          <FormField label="Pagina de IESAPP">
            {(field) => (
              <Select {...field} value={form.actionTarget ?? "/"} onChange={(e) => setForm({ ...form, actionTarget: e.target.value })}>
                {INTERNAL_ROUTES.map((route) => (
                  <option key={route} value={route}>{route === "/" ? "Inicio" : route}</option>
                ))}
              </Select>
            )}
          </FormField>
        )}
        {actionMode === "link" && (
          <FormField label="Enlace https">
            {(field) => <Input {...field} type="url" value={form.actionTarget ?? ""} onChange={(e) => setForm({ ...form, actionTarget: e.target.value || null })} placeholder="https://..." />}
          </FormField>
        )}

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Posicion">
            {(field) => <Input {...field} type="number" min={0} value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} />}
          </FormField>
          <FormField label="Estado">
            {(field) => (
              <Select {...field} value={form.status} disabled={contentLocked} onChange={(e) => setForm({ ...form, status: e.target.value as HomeBlockInput["status"] })}>
                <option value="draft">Borrador</option>
                <option value="published">Publicado</option>
              </Select>
            )}
          </FormField>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-ink">Habilitado</span>
          <Switch checked={form.enabled} onCheckedChange={(enabled) => setForm({ ...form, enabled })} aria-label="Bloque habilitado" />
        </div>

        <div className="border-t border-ink/10 pt-4">
          <button type="button" className="text-sm font-medium text-brand-700" onClick={() => setAdvancedOpen((value) => !value)}>
            {advancedOpen ? "Ocultar opciones avanzadas" : "Opciones avanzadas"}
          </button>
          {advancedOpen && (
            <div className="mt-3 space-y-4">
              <FormField label="Tipo tecnico">
                {(field) => (
                  <Select {...field} value={form.type} disabled={contentLocked} onChange={(e) => setForm({ ...form, type: e.target.value as HomeBlockType, metadata: {} })}>
                    {HOME_BLOCK_TYPES.map((type) => <option key={type} value={type}>{HOME_BLOCK_TYPE_LABELS[type]}</option>)}
                  </Select>
                )}
              </FormField>
              <FormField label="URL de imagen">
                {(field) => <Input {...field} value={form.imageUrl ?? ""} disabled={contentLocked} onChange={(e) => setForm({ ...form, imageUrl: e.target.value || null })} />}
              </FormField>
              <FormField label="Texto de accion">
                {(field) => <Input {...field} value={form.actionLabel ?? ""} disabled={contentLocked} onChange={(e) => setForm({ ...form, actionLabel: e.target.value || null })} />}
              </FormField>
              <FormField label="Destino de accion">
                {(field) => <Input {...field} value={form.actionTarget ?? ""} disabled={contentLocked} onChange={(e) => setForm({ ...form, actionTarget: e.target.value || null })} />}
              </FormField>
              <BlockMetadataFields type={form.type} metadata={form.metadata} disabled={contentLocked} onChange={(metadata) => setForm({ ...form, metadata })} />
            </div>
          )}
        </div>

        {error && <p role="alert" className="rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700">{error}</p>}

        <div className="flex justify-end gap-2 border-t border-ink/10 pt-4">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>Cancelar</Button>
          <Button type="submit" loading={submitting || uploading}>{editingBlock ? "Guardar cambios" : "Guardar"}</Button>
        </div>
      </form>
    </Drawer>
  );
}
